import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RectangleStackIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  getOnDemandSigningContainers,
  exportOnDemandSigningContainerCsv,
} from "../api/documents";
import { formatForDisplay } from "../utils/gmtUtils";
import Pagination from "./Pagination";
import {
  Button,
  Card,
  DataGrid,
  TableSkeleton,
  EmptyDocumentsIllustration,
} from "./ui";

const ITEMS_PER_PAGE = 10;

async function copyClipboardText(text, successMessage = "Copied to clipboard") {
  if (!text) {
    toast.error("Nothing to copy");
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error("Could not copy");
  }
}

const OnDemandSigningContainerList = ({ refreshVersion = 0 }) => {
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const fetchContainers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getOnDemandSigningContainers({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
      setContainers(response.containers || []);
      setTotalPages(response.totalPages ?? 1);
      setTotalItems(response.total ?? 0);
      if (response.page) setCurrentPage(response.page);
    } catch (err) {
      const msg =
        typeof err === "object" && err?.message
          ? err.message
          : "Failed to load containers";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchContainers();
  }, [fetchContainers, refreshVersion]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleRowClick = useCallback(
    (row) => {
      const id = row._id || row.id;
      if (id) navigate(`/dashboard/ondemand-signing/containers/${id}`);
    },
    [navigate],
  );

  const columns = [
    {
      key: "containerName",
      label: "Container",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <RectangleStackIcon className="h-4 w-4 text-primary" />
          </div>
          <span className="font-medium text-gray-900 truncate max-w-[200px]">
            {row.containerName}
          </span>
        </div>
      ),
    },
    {
      key: "importStatus",
      label: "Status",
      sortable: false,
      render: (_, row) => {
        const s = row.importStatus || "ready";
        if (s === "importing") {
          return (
            <span className="inline-flex items-center text-sm text-amber-700 font-medium">
              Importing…
            </span>
          );
        }
        if (s === "failed") {
          return (
            <span className="text-sm text-red-600 font-medium">Failed</span>
          );
        }
        return <span className="text-sm text-gray-600">Ready</span>;
      },
    },
    {
      key: "ownerEmail",
      label: "Owner email",
      sortable: false,
      render: (_, row) => (
        <a
          href={`mailto:${row.ownerEmail}`}
          className="text-primary hover:text-primary-dark hover:underline truncate block max-w-[220px]"
        >
          {row.ownerEmail}
        </a>
      ),
    },
    {
      key: "template",
      label: "Template",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-2 min-w-0">
          <DocumentTextIcon className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="text-gray-900 truncate max-w-[180px]">
            {row.templateId?.name || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "recipientCount",
      label: "Recipients",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-700 tabular-nums">
          {row.recipientCount ??
            (Array.isArray(row.recipients) ? row.recipients.length : 0)}
        </span>
      ),
    },
    {
      key: "ownerPortalUrl",
      label: "Owner portal",
      sortable: false,
      cellClassName: "whitespace-nowrap",
      render: (_, row) => {
        const url = row.ownerPortalUrl;
        if (!url) {
          return (
            <span
              className="text-xs text-gray-400"
              title="Set ON_DEMAND_OWNER_PORTAL_BASE_URL or PUBLIC_SITE_URL on the server to build this link."
            >
              —
            </span>
          );
        }
        return (
          <button
            type="button"
            onClick={() =>
              copyClipboardText(url, "Container link copied to clipboard")
            }
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/5"
          >
            <ClipboardDocumentIcon className="h-4 w-4 shrink-0" />
            Copy link
          </button>
        );
      },
    },
    {
      key: "exportCsv",
      label: "Export",
      sortable: false,
      cellClassName: "whitespace-nowrap",
      render: (_, row) => {
        const id = row._id || row.id;
        return (
          <button
            type="button"
            onClick={async () => {
              try {
                await exportOnDemandSigningContainerCsv(id, {
                  containerName: row.containerName,
                });
                toast.success("Export downloaded");
              } catch (err) {
                const msg =
                  err instanceof Error
                    ? err.message
                    : "Could not export container";
                toast.error(msg);
              }
            }}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 shrink-0" />
            CSV
          </button>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {formatForDisplay(row.createdAt, "MMM D, YYYY h:mm a")}
        </span>
      ),
    },
  ];

  const tableData = containers.map((c) => ({ ...c, id: c._id }));

  if (isLoading) {
    return (
      <div className="space-y-6 pt-2">
        <Card padding="none" className="overflow-hidden">
          <TableSkeleton rows={10} cols={8} showHeader />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-semibold text-gray-900">
            Containers
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Signing containers for your company, newest first. Select a row
            to open the full detail page.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => fetchContainers()}
          disabled={isLoading}
          leftIcon={ArrowPathIcon}
          className={isLoading ? "[&>svg]:animate-spin" : ""}
        >
          Refresh
        </Button>
      </div>

      <div>
        <DataGrid
          columns={columns}
          data={tableData}
          headerVariant="subtle"
          emptyMessage="No containers yet. Create one from the Create container tab."
          emptyIllustration={EmptyDocumentsIllustration}
          onRowClick={handleRowClick}
        />
        {totalPages > 0 && totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default OnDemandSigningContainerList;
